import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Visually rich collections — different from bento & featured-two
// Bento uses:        south-indian-traditional, hipchains, anti-tarnish, earrings, natural-stones
// Featured-two uses: bangles, rings
// These 4 are fresh:
const SLUGS = ['oxidised', 'south-indian-traditional', 'aamira-luxe', 'fashion'];

const FourCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const sectionRef                    = useRef(null);
  const isInView                      = useInView(sectionRef, { once: true, amount: 0.08 });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/collections`).then(r => r.json()),
      fetch(`${API}/products?active=true`).then(r => r.json()),
    ])
      .then(([colData, prodData]) => {
        if (colData.success) {
          const products = prodData.success ? prodData.products : [];
          const countMap = {};
          products.forEach(p => {
            if (Array.isArray(p.collection_slugs)) {
              p.collection_slugs.forEach(slug => {
                countMap[slug] = (countMap[slug] || 0) + 1;
              });
            }
          });

          const enriched = SLUGS
            .map(slug => {
              const col = colData.collections.find(c => c.slug === slug);
              if (!col) return null;
              return { ...col, productCount: countMap[slug] || 0 };
            })
            .filter(Boolean);

          setCollections(enriched);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        /* ─── Section ─── */
        .fc4-section {
          background: #F0EDE6;
          padding: 90px 0 80px;
          position: relative;
          overflow: hidden;
        }

        /* ─── Header ─── */
        .fc4-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 0 clamp(24px, 5vw, 80px);
          margin-bottom: 48px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .fc4-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .fc4-label::before {
          content: '';
          width: 28px;
          height: 1px;
          background: #B08D57;
          display: inline-block;
        }
        .fc4-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(30px, 4vw, 48px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .fc4-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .fc4-viewall {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          background: #1C1A18;
          text-decoration: none;
          padding: 14px 32px;
          display: inline-block;
          transition: background 0.22s;
          white-space: nowrap;
        }
        .fc4-viewall:hover { background: #B08D57; }

        /* ─── 2×2 Grid ─── */
        .fc4-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: 420px 420px;
          gap: 3px;
          padding: 0 clamp(24px, 5vw, 80px);
        }

        /* ─── Card ─── */
        .fc4-card {
          position: relative;
          overflow: hidden;
          cursor: pointer;
          display: block;
          text-decoration: none;
          background: #1C1A18;
        }

        /* image */
        .fc4-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.85s cubic-bezier(0.22, 1, 0.36, 1),
                      filter 0.5s ease;
          filter: brightness(0.8) saturate(0.9);
        }
        .fc4-card:hover img {
          transform: scale(1.07);
          filter: brightness(0.65) saturate(0.85);
        }

        .fc4-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(145deg, #2a2520, #1C1A18);
        }

        /* persistent dark gradient — bottom heavy like screenshot */
        .fc4-card::after {
          content: '';
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              to top,
              rgba(10,9,8,0.7) 0%,
              rgba(10,9,8,0.3) 42%,
              rgba(10,9,8,0.05) 70%,
              transparent 100%
            );
          z-index: 1;
          transition: background 0.35s;
        }
        .fc4-card:hover::after {
          background:
            linear-gradient(
              to top,
              rgba(10,9,8,0.8) 0%,
              rgba(10,9,8,0.4) 48%,
              rgba(10,9,8,0.1) 72%,
              transparent 100%
            );
        }

        /* thin gold left border that grows on hover */
        .fc4-border {
          position: absolute;
          left: 0;
          bottom: 0;
          width: 2px;
          height: 0;
          background: linear-gradient(to top, #B08D57, transparent);
          z-index: 3;
          transition: height 0.55s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .fc4-card:hover .fc4-border { height: 100%; }

        /* ─── Card content (mirrors screenshot layout) ─── */
        .fc4-content {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 22px 24px 24px;
        }

        /* top area: sub-label + badge */
        .fc4-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }

        .fc4-sub {
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: rgba(212,175,120,0.75);
        }

        .fc4-badge {
          font-family: 'Jost', sans-serif;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #1C1A18;
          background: #D4AF78;
          padding: 4px 10px;
        }

        /* bottom area */
        .fc4-bottom { display: flex; flex-direction: column; }

        .fc4-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(22px, 2.4vw, 34px);
          font-weight: 700;
          color: #fff;
          line-height: 1.12;
          margin-bottom: 16px;
          transition: color 0.3s;
        }

        /* explore link — exact match to screenshot */
        .fc4-explore {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #D4AF78;
          text-decoration: none;
          padding-bottom: 3px;
          border-bottom: 1px solid rgba(212,175,120,0.35);
          width: fit-content;
          transition: border-color 0.25s, color 0.25s, gap 0.25s;
        }
        .fc4-explore:hover {
          color: #fff;
          border-color: rgba(255,255,255,0.5);
          gap: 14px;
        }

        /* piece count pill */
        .fc4-count {
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.15em;
          color: rgba(212,175,120,0.5);
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .fc4-count-dot {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: #B08D57;
          display: inline-block;
        }

        /* ─── Skeleton ─── */
        .fc4-skeleton-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: 420px 420px;
          gap: 3px;
          padding: 0 clamp(24px, 5vw, 80px);
        }
        .fc4-skeleton {
          background: linear-gradient(90deg, #e4dfd6 25%, #ece7de 50%, #e4dfd6 75%);
          background-size: 200% 100%;
          animation: fc4-shimmer 1.5s infinite;
        }
        @keyframes fc4-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ─── Tablet ─── */
        @media (max-width: 900px) {
          .fc4-grid {
            grid-template-columns: repeat(2, 1fr);
            grid-template-rows: 320px 320px;
          }
          .fc4-skeleton-grid {
            grid-template-rows: 320px 320px;
          }
          .fc4-name { font-size: 20px; }
        }

        /* ─── Mobile ─── */
        @media (max-width: 580px) {
          .fc4-grid {
            grid-template-columns: 1fr;
            grid-template-rows: repeat(4, 300px);
            gap: 3px;
          }
          .fc4-skeleton-grid {
            grid-template-columns: 1fr;
            grid-template-rows: repeat(4, 300px);
          }
          .fc4-section  { padding: 60px 0 52px; }
          .fc4-name     { font-size: 22px; }
          /* always show explore on mobile */
          .fc4-explore  { opacity: 1; }
        }
      `}</style>

      <section className="fc4-section" ref={sectionRef}>

        {/* ─── Header ─── */}
        <motion.div
          className="fc4-header"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <p className="fc4-label">Discover More</p>
            <h2 className="fc4-title">
              Hand-picked <em>Collections</em>
            </h2>
          </div>
          <Link to="/collections" className="fc4-viewall">
            View All Collections
          </Link>
        </motion.div>

        {/* ─── Grid ─── */}
        {loading ? (
          <div className="fc4-skeleton-grid">
            {[0,1,2,3].map(i => <div key={i} className="fc4-skeleton" />)}
          </div>
        ) : collections.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px',
            fontFamily: "'Jost', sans-serif",
            fontSize: '13px', color: '#9A9186', letterSpacing: '0.1em',
          }}>
            No collections found.
          </div>
        ) : (
          <div className="fc4-grid">
            {collections.map((col, i) => (
              <motion.div
                key={col.id}
                className="fc4-card"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{
                  duration: 0.75,
                  delay: i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {/* Full-card link */}
                <Link
                  to={`/collections/${col.slug}`}
                  style={{ position: 'absolute', inset: 0, zIndex: 4, display: 'block' }}
                  aria-label={col.name}
                />

                {/* Gold left border */}
                <div className="fc4-border" />

                {/* Image */}
                {col.image_url
                  ? <img src={col.image_url} alt={col.name} loading="lazy" />
                  : <div className="fc4-placeholder" />
                }

                {/* Content layer */}
                <div className="fc4-content">

                  {/* Top row */}
                  <div className="fc4-top">
                    {col.sub && (
                      <span className="fc4-sub">{col.sub}</span>
                    )}
                    {col.badge && (
                      <span className="fc4-badge">{col.badge}</span>
                    )}
                  </div>

                  {/* Bottom row */}
                  <div className="fc4-bottom">
                    <div className="fc4-count">
                      <span className="fc4-count-dot" />
                      {col.productCount} {col.productCount === 1 ? 'piece' : 'pieces'}
                    </div>
                    <div className="fc4-name">{col.name}</div>
                    <span className="fc4-explore">
                      Explore →
                    </span>
                  </div>

                </div>
              </motion.div>
            ))}
          </div>
        )}

      </section>
    </>
  );
};

export default FourCollections;